// noncompliances.js
// Renders modal CONTENT only - the Modal shell is provided by homePage.js.
// homePage.js calls <ModalContent /> with no props, so this component takes none.
// All data-fetching and derived state lives in useNoncompliances (see hook file).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../../Modal.css";
import "../styles/noncompliances.css";
import { useNoncompliances } from "../../../hooks/useNoncompliances";

// ─── API status value constants ───────────────────────────────────────────────
const isComplete = (s) => s === "Complete";
const isNonConformance = (s) => s === "Non-Conformance";
const isCorrected = (s) => s === "Corrected";

// ─── Task grouping helper ─────────────────────────────────────────────────────
//
// The API returns one offsightTask row per submission event, so the same
// logical task (identified by taskDescription) can appear multiple times in
// station.tasks[]. This helper collapses them into groups:
//
//   [
//     {
//       taskDescription: string, - the shared display label
//       versions: Task[],        - all matching tasks, newest first
//       latestTask: Task,        - versions[0]
//       taskId: string,          - latestTask.taskId (used as group key)
//     },
//     …
//   ]
//
// Sort key: most recent step updatedAt across all steps in the task.
// Falls back to taskId string for deterministic ordering when timestamps match.

function latestStepTime(task) {
  let latest = 0;
  for (const step of task.steps ?? []) {
    const t = step.updatedAt ? new Date(step.updatedAt).getTime() : 0;
    if (t > latest) latest = t;
  }
  return latest;
}

function groupTasksByDescription(tasks) {
  const map = new Map();
  for (const task of tasks) {
    const key = task.taskDescription ?? "(unnamed)";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(task);
  }

  const groups = [];
  for (const [taskDescription, versions] of map) {
    versions.sort((a, b) => latestStepTime(b) - latestStepTime(a));
    groups.push({
      taskDescription,
      versions,
      latestTask: versions[0],
      taskId: versions[0].taskId,
    });
  }

  groups.sort((a, b) => {
    const hasNC = (g) =>
      g.versions.some((v) =>
        (v.steps ?? []).some(
          (s) => isNonConformance(s.status) || isCorrected(s.status),
        ),
      );
    const aNC = hasNC(a) ? 1 : 0;
    const bNC = hasNC(b) ? 1 : 0;
    if (bNC !== aNC) return bNC - aNC;
    return latestStepTime(b.latestTask) - latestStepTime(a.latestTask);
  });

  return groups;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

// MetricBox: renders as <button> when onClick is provided, <div> otherwise.
const MetricBox = ({
  title,
  value,
  variant = "neutral",
  onClick,
  active,
  loading,
}) => {
  const cls = [
    "nc-metric-box",
    `nc-metric-${variant}`,
    onClick ? "nc-metric-box--clickable" : "",
    active ? "nc-metric-box--active" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const displayValue = loading ? "-" : value;

  if (onClick) {
    return (
      <button
        type="button"
        className={cls}
        onClick={onClick}
        aria-pressed={active}
      >
        <div className="nc-metric-title">{title}</div>
        <div className="nc-metric-value">{displayValue}</div>
        <div className="nc-metric-cta">View all →</div>
      </button>
    );
  }
  return (
    <div className={cls}>
      <div className="nc-metric-title">{title}</div>
      <div className="nc-metric-value">{displayValue}</div>
    </div>
  );
};

const StatusBadge = ({ value }) => {
  const cls = {
    Complete: "badge-completed",
    "Non-Conformance": "badge-nonconformed",
    Corrected: "badge-corrected",
    "N/A": "badge-na",
    "Image Submitted": "badge-image",
    Signed: "badge-signed",
    "Pending Signature": "badge-pending-sig",
  };
  if (!value) return null;
  return <span className={`nc-badge ${cls[value] || ""}`}>{value}</span>;
};

// ── ImageLightbox ───────────────────────────────────────────────────────────────
//
// Fullscreen overlay for viewing task-step images at full size. Supports
// keyboard navigation and thumbnail strip when multiple images exist.

const ImageLightbox = ({ images, index, title, onClose, onNavigate }) => {
  const hasMultiple = images.length > 1;

  const goPrev = useCallback(() => {
    onNavigate((index - 1 + images.length) % images.length);
  }, [index, images.length, onNavigate]);

  const goNext = useCallback(() => {
    onNavigate((index + 1) % images.length);
  }, [index, images.length, onNavigate]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && hasMultiple) goPrev();
      else if (e.key === "ArrowRight" && hasMultiple) goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, goPrev, goNext, hasMultiple]);

  return (
    <div className="nc-lightbox-overlay" onClick={onClose}>
      <div className="nc-lightbox-body" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="nc-lightbox-close"
          onClick={onClose}
          aria-label="Close image viewer"
        >
          ×
        </button>

        <div className="nc-lightbox-stage">
          {hasMultiple && (
            <button
              type="button"
              className="nc-lightbox-nav nc-lightbox-nav--prev"
              onClick={goPrev}
              aria-label="Previous image"
            >
              ‹
            </button>
          )}
          <img
            src={images[index]}
            alt={`${title || "Task"} ${index + 1}`}
            className="nc-lightbox-image"
          />
          {hasMultiple && (
            <button
              type="button"
              className="nc-lightbox-nav nc-lightbox-nav--next"
              onClick={goNext}
              aria-label="Next image"
            >
              ›
            </button>
          )}
        </div>

        {hasMultiple && (
          <div className="nc-lightbox-thumbs">
            {images.map((img, i) => (
              <img
                key={i}
                src={img}
                alt=""
                className={`nc-lightbox-thumb ${
                  i === index ? "nc-lightbox-thumb--active" : ""
                }`}
                onClick={() => onNavigate(i)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── ModuleListItem ─────────────────────────────────────────────────────────────

const ModuleListItem = ({ item, selected, onClick }) => (
  <button
    type="button"
    className={`nc-module-item ${selected ? "nc-module-item--selected" : ""}`}
    onClick={onClick}
  >
    <div className="nc-module-item-main">
      <div className="nc-module-wo">{item.WoNum}</div>
      <div className="nc-module-meta">
        {item.ItemNum} &bull; {item.BatchNum}
      </div>
    </div>
    <div className="nc-module-time">{item.dateLabel}</div>
  </button>
);

// ── StationSummary ─────────────────────────────────────────────────────────────

const StationSummary = ({ station, taskGroups, onSelectGroup }) => {
  const { timeInStation, completionRate, nonconformanceCount, correctedCount } =
    station.summary;

  const { totalSteps, completeSteps } = useMemo(() => {
    let totalSteps = 0;
    let completeSteps = 0;
    station.tasks.forEach((t) =>
      (t.steps ?? []).forEach((st) => {
        totalSteps++;
        if (isComplete(st.status)) completeSteps++;
      }),
    );
    return { totalSteps, completeSteps };
  }, [station]);

  return (
    <div className="nc-station-summary">
      <h3 className="nc-section-title">
        Station Summary - {station.stationName}
      </h3>

      <div className="nc-station-metrics">
        <div className="nc-station-stat">
          <div className="nc-station-stat-label">Time in Station</div>
          <div className="nc-station-stat-value">{timeInStation || "-"}</div>
        </div>
        <div className="nc-station-stat">
          <div className="nc-station-stat-label">Completion Rate</div>
          <div className="nc-station-stat-value">
            {completionRate != null ? `${completionRate}%` : "-"}
          </div>
        </div>
        <div className="nc-station-stat">
          <div className="nc-station-stat-label">Non-Conformances</div>
          <div className="nc-station-stat-value">
            {nonconformanceCount ?? 0}
          </div>
        </div>
        <div className="nc-station-stat">
          <div className="nc-station-stat-label">Corrected</div>
          <div className="nc-station-stat-value">{correctedCount ?? 0}</div>
        </div>
      </div>

      <div className="nc-subheading">Tasks</div>
      <div className="nc-drilldown-table-wrap">
        <table className="nc-task-table">
          <thead>
            <tr>
              <th>Task</th>
              <th>Submissions</th>
              <th>Complete</th>
              <th>NC</th>
              <th>Corrected</th>
            </tr>
          </thead>
          <tbody>
            {taskGroups.length === 0 ? (
              <tr>
                <td colSpan={5} className="nc-empty nc-empty--center">
                  No tasks recorded.
                </td>
              </tr>
            ) : (
              taskGroups.map((group) => {
                const tc = { comp: 0, nc: 0, corr: 0 };
                group.versions.forEach((v) =>
                  (v.steps ?? []).forEach((st) => {
                    if (isComplete(st.status)) tc.comp++;
                    else if (isNonConformance(st.status)) tc.nc++;
                    else if (isCorrected(st.status)) tc.corr++;
                  }),
                );
                return (
                  <tr
                    key={group.taskId}
                    className="nc-task-row"
                    onClick={() => onSelectGroup(group.taskId)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <span className="nc-task-desc-cell">
                        {group.taskDescription ?? "-"}
                        {group.versions.length > 1 && (
                          <span className="nc-version-pill">
                            {group.versions.length} submissions
                          </span>
                        )}
                      </span>
                    </td>
                    <td>{group.versions.length}</td>
                    <td className="nc-cell-green">{tc.comp}</td>
                    <td className={tc.nc > 0 ? "nc-cell-red" : ""}>{tc.nc}</td>
                    <td className={tc.corr > 0 ? "nc-cell-amber" : ""}>
                      {tc.corr}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── TaskDetail ─────────────────────────────────────────────────────────────────

function formatVersionLabel(task, index, total) {
  const submissionNum = total - index;
  const date = task.steps?.[0]?.updatedAt
    ? new Date(task.steps[0].updatedAt)
    : null;
  const dateStr = date
    ? date.toLocaleString(undefined, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";
  return `Submission ${submissionNum} - ${dateStr}`;
}

const TaskDetail = ({ taskGroup }) => {
  const { versions, taskDescription } = taskGroup;
  const [versionIndex, setVersionIndex] = useState(0);
  const activeTask = versions[versionIndex] ?? versions[0];
  // Lightbox state: { images, index, stepName } for the step whose images
  // are currently enlarged, or null when the lightbox is closed.
  const [lightbox, setLightbox] = useState(null);

  return (
    <div className="nc-task-detail">
      <div className="nc-task-detail-header">
        <h3 className="nc-section-title">{taskDescription ?? "-"}</h3>

        {versions.length > 1 && (
          <div className="nc-version-selector">
            <label
              className="nc-version-selector-label"
              htmlFor="nc-version-select"
            >
              Submission
            </label>
            <select
              id="nc-version-select"
              className="nc-version-select"
              value={versionIndex}
              onChange={(e) => setVersionIndex(Number(e.target.value))}
            >
              {versions.map((v, i) => (
                <option key={v.taskId} value={i}>
                  {formatVersionLabel(v, i, versions.length)}
                  {i === 0 ? " (latest)" : ""}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {versions.length === 1 && (
        <div className="nc-version-single-tag">
          <span className="nc-version-tag nc-version-tag--latest">
            {formatVersionLabel(versions[0], 0, 1)}
          </span>
        </div>
      )}

      <div className="nc-version-meta-strip">
        {activeTask.steps?.[0]?.updatedBy && (
          <span className="nc-version-meta-item">
            <span className="nc-version-meta-key">Submitted by</span>
            {activeTask.steps[0].updatedBy}
          </span>
        )}
        {activeTask.taskGroupName && (
          <span className="nc-version-meta-item">
            <span className="nc-version-meta-key">Group</span>
            {activeTask.taskGroupName}
          </span>
        )}
        {activeTask.zoneName && (
          <span className="nc-version-meta-item">
            <span className="nc-version-meta-key">Zone</span>
            {activeTask.zoneName}
          </span>
        )}
      </div>

      <div className="nc-subtask-list">
        {(activeTask.steps ?? []).length === 0 ? (
          <div className="nc-empty nc-empty--center">No data found</div>
        ) : (
          (activeTask.steps ?? []).map((st) => (
            <div key={st.stepName} className="nc-subtask-row">
              <div className="nc-subtask-header">
                <span className="nc-subtask-name">{st.stepName}</span>
                <StatusBadge value={st.status} />
              </div>
              <div className="nc-subtask-meta">
                <span>{new Date(st.updatedAt).toLocaleString()}</span>
                {st.updatedBy && (
                  <>
                    <span className="nc-meta-sep">&bull;</span>
                    <span>{st.updatedBy}</span>
                  </>
                )}
                {st.stepComment && (
                  <>
                    <span className="nc-meta-sep">&bull;</span>
                    <span className="nc-subtask-comment">{st.stepComment}</span>
                  </>
                )}
                {st.reasons?.length > 0 && (
                  <>
                    <span className="nc-meta-sep">&bull;</span>
                    <span className="nc-subtask-reasons">
                      {st.reasons.join(", ")}
                    </span>
                  </>
                )}
              </div>
              {st.images?.length > 0 && (
                <div className="nc-subtask-images">
                  {st.images.map((img, ii) => (
                    <button
                      key={ii}
                      type="button"
                      className="nc-thumb-wrap"
                      onClick={() =>
                        setLightbox({
                          images: st.images,
                          index: ii,
                          stepName: st.stepName,
                        })
                      }
                      aria-label={`Enlarge image ${ii + 1} for ${st.stepName}`}
                    >
                      <img
                        src={img}
                        alt={`${st.stepName} ${ii + 1}`}
                        className="nc-thumb"
                      />
                      <span className="nc-thumb-zoom-icon" aria-hidden="true">
                        ⤢
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {st.hasVersionHistory && st.versionHistory?.length > 0 && (
                <details className="nc-version-history">
                  <summary>Step history ({st.versionHistory.length})</summary>
                  <ul className="nc-version-list">
                    {st.versionHistory.map((v, vi) => (
                      <li key={vi} className="nc-version-item">
                        <div className="nc-version-top">
                          <StatusBadge value={v.status} />
                          <span className="nc-version-date">
                            {new Date(v.updatedAt).toLocaleString()}
                          </span>
                        </div>
                        <div className="nc-version-by">
                          {v.updatedBy ?? "-"}
                          {v.stepComment ? ` - ${v.stepComment}` : ""}
                        </div>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          ))
        )}
      </div>

      {lightbox && (
        <ImageLightbox
          images={lightbox.images}
          index={lightbox.index}
          title={lightbox.stepName}
          onClose={() => setLightbox(null)}
          onNavigate={(newIndex) =>
            setLightbox((prev) => (prev ? { ...prev, index: newIndex } : prev))
          }
        />
      )}
    </div>
  );
};

// ── MetricTasksTable ───────────────────────────────────────────────────────────
//
// Renders the real items[] returned by fetchNoncompliancesMetricTasks(). Each
// row is a module (WO / Batch / Item) that is expandable to show its individual
// steps inline.
//
// Props:
//   items  - Array from metricTasksItems (hook)
//   status - metricTasksStatus
//   error  - metricTasksError

const MetricTasksTable = ({ items, status, error }) => {
  // Track which manufacturingItemIds are expanded (show steps).
  const [expandedIds, setExpandedIds] = useState(new Set());

  const toggleExpand = (id) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  if (status === "loading") {
    return <div className="nc-empty nc-empty--center">Loading details…</div>;
  }

  if (status === "error") {
    return (
      <div className="nc-error-banner">Failed to load details: {error}</div>
    );
  }

  if (!items.length) {
    return <div className="nc-empty nc-empty--center">No items for today.</div>;
  }

  return (
    <div className="nc-drilldown-table-wrap">
      <table className="nc-task-table nc-drilldown-table">
        <thead>
          <tr>
            <th>WO #</th>
            <th>Item #</th>
            <th>Batch #</th>
            <th>Steps</th>
            <th>{/* expand toggle column */}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const isExpanded = expandedIds.has(item.manufacturingItemId);
            return (
              <React.Fragment key={item.manufacturingItemId}>
                {/* ── Module summary row ── */}
                <tr
                  className="nc-task-row nc-task-row--module"
                  onClick={() => toggleExpand(item.manufacturingItemId)}
                  style={{ cursor: "pointer" }}
                >
                  <td>{item.woNum ?? "-"}</td>
                  <td>{item.itemNum ?? "-"}</td>
                  <td>{item.batchNum ?? "-"}</td>
                  <td>{item.steps.length}</td>
                  <td className="nc-expand-toggle">{isExpanded ? "▲" : "▼"}</td>
                </tr>

                {/* ── Expanded steps ── */}
                {isExpanded && (
                  <tr className="nc-task-row--steps-row">
                    <td colSpan={5} className="nc-steps-cell">
                      <table className="nc-task-table nc-steps-table">
                        <thead>
                          <tr>
                            <th>Station</th>
                            <th>Task</th>
                            <th>Step</th>
                            <th>Status</th>
                            <th>Updated</th>
                            <th>By</th>
                            <th>Comment</th>
                            <th>Reasons</th>
                          </tr>
                        </thead>
                        <tbody>
                          {item.steps.length === 0 ? (
                            <tr>
                              <td
                                colSpan={8}
                                className="nc-empty nc-empty--center"
                              >
                                No steps recorded.
                              </td>
                            </tr>
                          ) : (
                            item.steps.map((step, si) => (
                              <tr key={si} className="nc-subtask-row--table">
                                <td>{step.stationName ?? "-"}</td>
                                <td>{step.taskDescription ?? "-"}</td>
                                <td>{step.stepName ?? "-"}</td>
                                <td>
                                  <StatusBadge value={step.stepStatus} />
                                </td>
                                <td>
                                  {step.updatedAt
                                    ? new Date(step.updatedAt).toLocaleString()
                                    : "-"}
                                </td>
                                <td>{step.updatedBy ?? "-"}</td>
                                <td>{step.stepComment ?? "-"}</td>
                                <td>
                                  {step.reasons?.length > 0
                                    ? step.reasons.join(", ")
                                    : "-"}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── DrillDownPanel ─────────────────────────────────────────────────────────────
//
// Receives metricTasksItems / metricTasksStatus / metricTasksError and passes
// them straight into MetricTasksTable.

const DrillDownPanel = ({
  label,
  variant,
  count,
  onBack,
  metricTasksItems,
  metricTasksStatus,
  metricTasksError,
}) => (
  <div className="nc-drilldown-panel">
    <div className="nc-drilldown-header">
      <button
        type="button"
        className="nc-drilldown-back"
        onClick={onBack}
        aria-label="Back to module view"
      >
        ← Back
      </button>
      <div className={`nc-drilldown-title nc-drilldown-title--${variant}`}>
        {label}
        <span className="nc-drilldown-count">
          {count} module{count !== 1 ? "s" : ""}
        </span>
      </div>
    </div>
    <div className="nc-drilldown-content">
      <MetricTasksTable
        items={metricTasksItems}
        status={metricTasksStatus}
        error={metricTasksError}
      />
    </div>
  </div>
);

// ─── Main export ──────────────────────────────────────────────────────────────

const Noncompliances = () => {
  const {
    // Seed
    seedStatus,
    seedError,
    jobsCC,
    jobsUE,
    jobsNC,

    // List
    listItems,
    listStatus,
    listError,
    hasSearch,
    hasOnlineMods,

    // Search
    search,
    setSearch,
    isSearching,

    // Selection
    selectedBatchNum,
    selectMod,

    // Detail
    detailStatus,
    detailError,
    detailData,

    // Sub-panel nav
    selectedStationId,
    setSelectedStationId,
    selectedTaskId,
    setSelectedTaskId,

    // Metric drill-down
    activeMetric,
    setActiveMetric,

    // Metric-tasks
    metricTasksStatus,
    metricTasksError,
    metricTasksItems,
  } = useNoncompliances();

  // ── Derived detail selections ───────────────────────────────────────────────
  const stations = detailData?.stations ?? [];
  const header = detailData?.header ?? null;

  const selectedStation =
    stations.find((s) => s.stationId === selectedStationId) ?? null;

  // ── Task grouping ────────────────────────────────────────────────────────────
  const taskGroups = useMemo(
    () => groupTasksByDescription(selectedStation?.tasks ?? []),
    [selectedStation],
  );

  // ── Selected group ───────────────────────────────────────────────────────────
  const selectedGroup =
    taskGroups.find((g) => g.taskId === selectedTaskId) ?? null;

  // ── Metric-box click handler (toggle) ────────────────────────────────────────
  const handleMetricClick = (key) =>
    setActiveMetric((prev) => (prev === key ? null : key));

  // ── Drill-down config ────────────────────────────────────────────────────────
  const drillDownConfig = {
    completed: {
      label: "Completed Correctly",
      variant: "green",
      count: jobsCC,
    },
    unevaluated: { label: "Unevaluated", variant: "amber", count: jobsUE },
    nonconformed: { label: "Nonconformed", variant: "red", count: jobsNC },
  };
  const activeDrill = activeMetric ? drillDownConfig[activeMetric] : null;

  const seedLoading = seedStatus === "loading";

  // ── Left-panel empty-state resolver ─────────────────────────────────────────
  //
  // Derived PURELY from the current search value and seed status - never from
  // the async listStatus - so it can never lag a render behind and append
  // stale text under the list. Only ever rendered when listItems.length === 0,
  // as the single alternate branch of the list render (mutually exclusive with
  // the mapped items - they can never both appear).
  //
  //   Search active            → "No module found"
  //   No search + seed loading  → "Loading data..."
  //   No search + none on line  → "No modules On Line"
  const listEmptyMessage = hasSearch
    ? "No module found"
    : seedLoading
      ? "Loading data..."
      : "No modules On Line";

  // ──────────────────────────────────────────────────────────────────────────
  return (
    <div className="nc-root">
      {/* ── Top 3 metric boxes ─────────────────────────────────────────────── */}
      <div className="nc-top-metrics">
        <MetricBox
          title="Completed Correctly"
          value={jobsCC}
          variant="green"
          loading={seedLoading}
          active={activeMetric === "completed"}
          onClick={() => handleMetricClick("completed")}
        />
        <MetricBox
          title="Unevaluated"
          value={jobsUE}
          variant="amber"
          loading={seedLoading}
          active={activeMetric === "unevaluated"}
          onClick={() => handleMetricClick("unevaluated")}
        />
        <MetricBox
          title="Nonconformed"
          value={jobsNC}
          variant="red"
          loading={seedLoading}
          active={activeMetric === "nonconformed"}
          onClick={() => handleMetricClick("nonconformed")}
        />
      </div>

      {seedStatus === "error" && (
        <div className="nc-error-banner">
          Failed to load noncompliance data: {seedError}
        </div>
      )}

      {activeDrill ? (
        <DrillDownPanel
          label={activeDrill.label}
          variant={activeDrill.variant}
          count={activeDrill.count}
          onBack={() => setActiveMetric(null)}
          metricTasksItems={metricTasksItems}
          metricTasksStatus={metricTasksStatus}
          metricTasksError={metricTasksError}
        />
      ) : (
        <div className="nc-body">
          {/* LEFT - module list */}
          <div className="nc-left">
            <div className="nc-search-row">
              <input
                className="nc-search-input"
                placeholder="Search WO, Batch, or Item..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search modules"
              />
              <span className="nc-search-count">
                {isSearching ? "…" : listItems.length}
              </span>
            </div>

            {listStatus === "error" && (
              <div className="nc-error-banner">{listError}</div>
            )}

            <div className="nc-module-list">
              {listItems.length > 0 ? (
                <div className="nc-module-list-items" key="nc-list-items">
                  {listItems.map((item) => (
                    <ModuleListItem
                      key={item.key}
                      item={item}
                      selected={item.BatchNum === selectedBatchNum}
                      onClick={() => selectMod(item.BatchNum)}
                    />
                  ))}
                </div>
              ) : (
                <div className="nc-empty nc-empty--center" key="nc-list-empty">
                  {listEmptyMessage}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT - detail panel */}
          <div className="nc-right">
            {!selectedBatchNum ? (
              <div className="nc-empty nc-empty--center">
                Select a mod from the list or search for one.
              </div>
            ) : detailStatus === "loading" ? (
              <div className="nc-empty nc-empty--center">Loading data...</div>
            ) : detailStatus === "error" ? (
              <div className="nc-error-banner">{detailError}</div>
            ) : detailData && header ? (
              <>
                {/* Module header */}
                <div className="nc-module-header">
                  <div>
                    <div className="nc-module-header-title">{header.woNum}</div>
                    <div className="nc-module-header-sub">
                      {header.itemNum} &bull; {header.batchNum}
                    </div>
                  </div>
                  <div className="nc-module-header-dates">
                    <div>
                      Created:{" "}
                      {header.createdAt
                        ? new Date(header.createdAt).toLocaleDateString()
                        : "-"}
                    </div>
                    <div>
                      Last Seen:{" "}
                      {header.lastSeenAt
                        ? new Date(header.lastSeenAt).toLocaleDateString()
                        : "-"}
                    </div>
                  </div>
                </div>

                {/* Station / task selectors */}
                <div className="nc-controls">
                  <label className="nc-control-label">
                    <span>Station</span>
                    <select
                      value={selectedStationId ?? ""}
                      onChange={(e) =>
                        setSelectedStationId(Number(e.target.value))
                      }
                    >
                      {stations.map((s) => (
                        <option key={s.stationId} value={s.stationId}>
                          {s.stationName}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="nc-control-label">
                    <span>Task</span>
                    <select
                      value={selectedTaskId ?? ""}
                      onChange={(e) =>
                        setSelectedTaskId(e.target.value || null)
                      }
                      disabled={!selectedStation}
                    >
                      <option value="">- Station Summary -</option>
                      {taskGroups.map((group) => (
                        <option key={group.taskId} value={group.taskId}>
                          {group.taskDescription ?? "-"}
                          {group.versions.length > 1
                            ? ` (${group.versions.length} submissions)`
                            : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                {/* Content area */}
                <div className="nc-content">
                  {selectedGroup ? (
                    <TaskDetail taskGroup={selectedGroup} />
                  ) : selectedStation ? (
                    <StationSummary
                      station={selectedStation}
                      taskGroups={taskGroups}
                      onSelectGroup={(taskId) => setSelectedTaskId(taskId)}
                    />
                  ) : (
                    <div className="nc-empty nc-empty--center">
                      Select a station above.
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default Noncompliances;
